import {
    alpha,
    iconButtonClasses,
    Popper,
    type PopperProps,
    type Theme,
    type ThemeOptions,
} from '@mui/material';
import { gridClasses } from '@mui/x-data-grid-pro';
import '@mui/x-data-grid-pro/themeAugmentation';
import { math } from 'polished';
import { type AnchorHTMLAttributes, forwardRef, useCallback, useState } from 'react';

import LibreFranklinBoldTtf from '@@fonts/LibreFranklin-Bold.ttf';
import LibreFranklinBoldWoff from '@@fonts/LibreFranklin-Bold.woff';
import LibreFranklinBoldWoff2 from '@@fonts/LibreFranklin-Bold.woff2';
import LibreFranklinRegularTtf from '@@fonts/LibreFranklin-Regular.ttf';
import LibreFranklinRegularWoff from '@@fonts/LibreFranklin-Regular.woff';
import LibreFranklinRegularWoff2 from '@@fonts/LibreFranklin-Regular.woff2';
import SoleSerifHeadlineWMdItalicTtf from '@@fonts/SoleSerifHeadline_W_MdIt.ttf';
import SoleSerifHeadlineWMdItalicWoff from '@@fonts/SoleSerifHeadline_W_MdIt.woff';
import SoleSerifHeadlineWMdItalicWoff2 from '@@fonts/SoleSerifHeadline_W_MdIt.woff2';
import SoleSerifTextWBoldTtf from '@@fonts/SoleSerifText_W_Bd.ttf';
import SoleSerifTextWBoldWoff from '@@fonts/SoleSerifText_W_Bd.woff';
import SoleSerifTextWBoldWoff2 from '@@fonts/SoleSerifText_W_Bd.woff2';
import SoleSerifTextWRegularTtf from '@@fonts/SoleSerifText_W_Rg.ttf';
import SoleSerifTextWRegularWoff from '@@fonts/SoleSerifText_W_Rg.woff';
import SoleSerifTextWRegularWoff2 from '@@fonts/SoleSerifText_W_Rg.woff2';
import SoleSerifTextWRegularItalicTtf from '@@fonts/SoleSerifText_W_RgIt.ttf';
import SoleSerifTextWRegularItalicWoff from '@@fonts/SoleSerifText_W_RgIt.woff';
import SoleSerifTextWRegularItalicWoff2 from '@@fonts/SoleSerifText_W_RgIt.woff2';
import { getRel } from '@@utils/browser';
import { updateRef } from '@@utils/index';

import {
    BUTTON_HEIGHT_LARGE,
    BUTTON_HEIGHT_MEDIUM,
    BUTTON_HEIGHT_SMALL,
    ANCHOR_CELL,
    CLICKABLE_ROW,
    DATA_GRID_ROW_HEIGHT,
    DATA_GRID_VARS,
    DISABLED_ROW,
    HOVER_ONLY_CELL,
    TOP_ALIGNED_CELL,
    WRAPPED_CELL,
} from '../constants';
import { resolveColor } from '../utils';

const ONE_AND_A_HALF = 1.5;
// Using the same value as MUI is using internally (for tooltip background color).
const TOOLTIP_ALPHA_VALUE = 0.92;

const getListItemIndent = (theme: Theme, level?: number) =>
    level != null && level >= 0
        ? theme.spacing(2) + ' + ' + theme.spacing(4) + ' * ' + (level + 1) * ONE_AND_A_HALF
        : '0px';

// @ts-expect-error TS7006 Issue #4120
const getListItemPadding = (theme, level) => ({
    paddingLeft: math(theme.spacing(4) + ' + ' + getListItemIndent(theme, level)),
    paddingRight: theme.spacing(4),
});

// https://mui.com/material-ui/integrations/routing/#global-theme-link
// Links into the app are typed router links (`RouterLink`, `RouterButton`, `createLink`), so an href
// leaves the app
export const LinkBehavior = forwardRef<HTMLAnchorElement, AnchorHTMLAttributes<HTMLAnchorElement>>(
    (props, ref) => {
        const { rel = getRel(props.target), ...other } = props;

        return <a {...other} ref={ref} rel={rel} />;
    },
);

// By default popper renders components using a portal which injects elements into `document.body`. This generates
// problems, if we want to display popper components within modal dialogs, since we use the native dialog element,
// which renders modal dialogs in the top layer, which will always be displayed ABOVE everything else which is not
// rendered in the top layer (like stuff we inject into `document.body`).
// This `PopperComponent` tries to address this issue in a central place, by rendering the component inside the
// dialog element instead of the body, IF the component is actually inside a dialog.
const PopperComponent = forwardRef<HTMLElement, PopperProps>((props, ref) => {
    const [container, setContainer] = useState<HTMLElement | null>(null);

    const refCallback = useCallback(
        // @ts-expect-error TS7006 Issue #4120
        (instance) => {
            updateRef(ref, instance);

            if (instance) {
                setContainer((container) =>
                    !container ? instance.closest('body, dialog') : container,
                );
            }
        },
        [ref],
    );

    if (!props.disablePortal) {
        return (
            <Popper ref={refCallback} disablePortal={!container} container={container} {...props} />
        );
    }

    return <Popper {...props} />;
});

const components: ThemeOptions['components'] = {
    MuiCssBaseline: {
        styleOverrides: {
            '@font-face': {
                fontFamily: '"Sole Serif"',
                fontStyle: 'normal',
                fontWeight: 400,
                fontDisplay: 'swap',
                src: `url(${SoleSerifTextWRegularWoff2}) format('woff2'),
                    url(${SoleSerifTextWRegularWoff}) format('woff'),
                    url(${SoleSerifTextWRegularTtf}) format('truetype')`,
            },
            fallbacks: [
                {
                    '@font-face': {
                        fontFamily: '"Sole Serif"',
                        fontStyle: 'italic',
                        fontWeight: 400,
                        fontDisplay: 'swap',
                        src: `url(${SoleSerifTextWRegularItalicWoff2}) format('woff2'),
                            url(${SoleSerifTextWRegularItalicWoff}) format('woff'),
                            url(${SoleSerifTextWRegularItalicTtf}) format('truetype')`,
                    },
                },
                {
                    '@font-face': {
                        fontFamily: '"Sole Serif"',
                        fontStyle: 'normal',
                        fontWeight: 700,
                        fontDisplay: 'swap',
                        src: `url(${SoleSerifTextWBoldWoff2}) format('woff2'),
                            url(${SoleSerifTextWBoldWoff}) format('woff'),
                            url(${SoleSerifTextWBoldTtf}) format('truetype')`,
                    },
                },
                {
                    '@font-face': {
                        fontFamily: '"Sole Serif"',
                        fontStyle: 'italic',
                        fontWeight: 500,
                        fontDisplay: 'swap',
                        src: `url(${SoleSerifHeadlineWMdItalicWoff2}) format('woff2'),
                            url(${SoleSerifHeadlineWMdItalicWoff}) format('woff'),
                            url(${SoleSerifHeadlineWMdItalicTtf}) format('truetype')`,
                    },
                },
                {
                    '@font-face': {
                        fontFamily: '"Libre Franklin"',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontDisplay: 'swap',
                        src: `url(${LibreFranklinRegularWoff2}) format('woff2'),
                            url(${LibreFranklinRegularWoff}) format('woff'),
                            url(${LibreFranklinRegularTtf}) format('truetype')`,
                    },
                },
                {
                    '@font-face': {
                        fontFamily: '"Libre Franklin"',
                        fontStyle: 'normal',
                        fontWeight: 700,
                        fontDisplay: 'swap',
                        src: `url(${LibreFranklinBoldWoff2}) format('woff2'),
                            url(${LibreFranklinBoldWoff}) format('woff'),
                            url(${LibreFranklinBoldTtf}) format('truetype')`,
                    },
                },
                // Is not yet used in this project..
                // {
                //     '@font-face': {
                //         fontFamily: '"Libre Franklin"',
                //         fontStyle: 'normal',
                //         fontWeight: 900,
                //         fontDisplay: 'swap',
                //         src: `url(${LibreFranklinBlackWoff2}) format('woff2'),
                //             url(${LibreFranklinBlackWoff}) format('woff'),
                //             url(${LibreFranklinBlackTtf}) format('truetype')`,
                //     },
                // },
            ],
        },
    },
    MuiButton: {
        styleOverrides: {
            root: {
                // Avoid breaking text
                flexShrink: 0,
                whiteSpace: 'nowrap',
            },
            sizeSmall: {
                height: `${BUTTON_HEIGHT_SMALL}px`,
            },
            sizeMedium: {
                height: `${BUTTON_HEIGHT_MEDIUM}px`,
            },
            sizeLarge: {
                height: `${BUTTON_HEIGHT_LARGE}px`,
            },
        },
        defaultProps: {
            loadingPosition: 'start',
        },
    },
    MuiButtonBase: {
        defaultProps: {
            LinkComponent: LinkBehavior,
            disableRipple: import.meta.env.MODE === 'test',
            disableTouchRipple: import.meta.env.MODE === 'test',
        },
    },
    MuiCard: {
        defaultProps: {
            square: true,
        },
        styleOverrides: {
            root: ({ ownerState: { color }, theme }) => ({
                // Make sure any absolute positioned element inside a card is not escaping it's boundaries
                position: 'relative',
                // Avoid cutting off absolute positioned elements that cross the cards boundaries
                overflow: 'visible',
                display: 'flex',
                flexDirection: 'column',
                '&::before': {
                    ...(color && {
                        position: 'absolute',
                        top: 0,
                        content: '""',
                        display: 'block',
                        width: '100%',
                        height: theme.borders[3],
                        backgroundColor: resolveColor(theme, color),
                    }),
                },
            }),
        },
    },
    MuiCardHeader: {
        defaultProps: {
            slotProps: {
                title: {
                    variant: 'h3',
                },
            },
        },
        styleOverrides: {
            root: ({ theme }) => ({
                padding: theme.spacing(4),
                // If this card header has a next sibling, which is not hidden and not a card header,
                // reduce padding-bottom
                // `:where` will reduce specifity to 0 and the `&` at the end will set it to the same level as
                // normal styles. This we do, in order to allow overriding those styles easily externally,
                // without having to know these internal rules.
                ':where(:has(+ *:not([aria-hidden="true"]):not(.MuiCardMedia-root)))&': {
                    paddingBottom: theme.spacing(2),
                },
            }),
            title: {
                display: 'flex',
                alignItems: 'center',
            },
            action: ({ theme }) => ({
                display: 'flex',
                alignItems: 'center',
                alignSelf: 'auto',
                gap: theme.spacing(1),
            }),
        },
    },
    MuiTableCell: {
        styleOverrides: {
            root: ({ theme }) => ({
                ...theme.typography.small,
                borderColor: theme.palette.primary['100'],
                '&:first-of-type': {
                    paddingLeft: theme.spacing(4),
                },
                '&:last-of-type': {
                    paddingRight: theme.spacing(4),
                },
            }),
        },
    },
    MuiTableRow: {
        styleOverrides: {
            root: {
                verticalAlign: 'top',
            },
        },
    },
    MuiDataGrid: {
        defaultProps: {
            columnHeaderHeight: 40,
            rowHeight: DATA_GRID_ROW_HEIGHT,
            // Every grid navigates or is read-only, so a click never selects.
            disableRowSelectionOnClick: true,
        },
        styleOverrides: {
            root: ({ theme }: { theme: Theme }) => ({
                ...theme.typography.small,
                border: 'none',
                [DATA_GRID_VARS.rowBorderColor]: theme.palette.primary['100'],
                [DATA_GRID_VARS.cellPadding]: `${theme.fixed.dataGrid.cellPadding.standard}px`,
                [DATA_GRID_VARS.cellInset]: `${theme.fixed.dataGrid.edgeInset}px`,

                [`&.${gridClasses['root--densityCompact']}`]: {
                    [DATA_GRID_VARS.cellPadding]: `${theme.fixed.dataGrid.cellPadding.compact}px`,

                    // The header shrinks with the density, so its buttons give up their padding.
                    [`& .${gridClasses.columnHeader} .${iconButtonClasses.root}`]: {
                        padding: 0,
                    },
                },

                [`&.${gridClasses['root--densityComfortable']}`]: {
                    [DATA_GRID_VARS.cellPadding]: `${theme.fixed.dataGrid.cellPadding.comfortable}px`,
                },

                [`& .${gridClasses.cell}[data-colindex='0'], & .${gridClasses.columnHeader}[aria-colindex='1']`]:
                    {
                        paddingLeft: `var(${DATA_GRID_VARS.cellInset})`,
                    },

                [`& .${gridClasses['cell--pinnedRight']}, & .${gridClasses['columnHeader--last']}`]:
                    {
                        paddingRight: `var(${DATA_GRID_VARS.cellInset})`,
                    },

                [`& .${gridClasses.main}:not(.${gridClasses['main--hasPinnedRight']}) .${gridClasses.cell}:has(+ .${gridClasses.cellEmpty})`]:
                    {
                        paddingRight: `var(${DATA_GRID_VARS.cellInset})`,
                    },

                // only the keyboard gets the focus outline.
                [`& .${gridClasses.cell}:not(:focus-visible):not(:has(:focus-visible))`]: {
                    outline: 'none',
                },

                [`& .${gridClasses.cell}.${HOVER_ONLY_CELL} > *`]: {
                    opacity: 0,
                },

                [`& .${gridClasses.row}:hover .${gridClasses.cell}.${HOVER_ONLY_CELL} > *, & .${gridClasses.cell}.${HOVER_ONLY_CELL}:focus-within > *`]:
                    {
                        opacity: 1,
                    },
            }),
            columnHeaders: ({ theme }: { theme: Theme }) => ({
                [DATA_GRID_VARS.containerBackground]: theme.palette.common.white,
            }),
            columnHeader: {
                paddingLeft: `var(${DATA_GRID_VARS.cellPadding})`,
                paddingRight: `var(${DATA_GRID_VARS.cellPadding})`,
            },
            row: {
                [`&.${CLICKABLE_ROW}`]: {
                    cursor: 'pointer',
                },

                [`&.${DISABLED_ROW}`]: {
                    filter: 'opacity(0.5)',
                    pointerEvents: 'none',
                },
            },
            columnHeaderTitle: ({ theme }: { theme: Theme }) => ({
                ...theme.typography.small,
                fontWeight: theme.typography.fontWeightBold,
            }),
            footerContainer: ({ theme }: { theme: Theme }) => ({
                padding: theme.spacing(4),
            }),
            cell: ({ theme }: { theme: Theme }) => ({
                padding: `var(${DATA_GRID_VARS.cellPadding})`,
                // Data Grid centers cells with a row-height line height, which children inherit.
                lineHeight: theme.typography.small.lineHeight,
                alignContent: 'center',

                [`&.${TOP_ALIGNED_CELL}`]: {
                    alignContent: 'start',
                },

                [`&.${WRAPPED_CELL}`]: {
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                },

                [`&.${ANCHOR_CELL}`]: {
                    position: 'relative',
                },

                overflow: 'hidden',

                // The filler before the pinned columns is a cell too, and padding would stop it
                // from shrinking to nothing when the columns overflow.
                [`&.${gridClasses.cellEmpty}`]: {
                    padding: 0,
                },
            }),
            'cell--withLeftBorder': {
                borderLeft: 'none',
            },
            'columnHeader--withLeftBorder': {
                borderLeft: 'none',
            },
            cellSkeleton: {
                alignItems: 'center',
            },
        },
    },
    MuiCardContent: {
        styleOverrides: {
            root: ({ theme }) => ({
                display: 'flex',
                padding: theme.spacing(4),
                '&:last-child': {
                    paddingBottom: theme.spacing(4),
                },
                // If the next sibling is a card content, reduce padding-bottom
                // `:where` will reduce specifity to 0 and the `&` at the end will set it to the same level as
                // normal styles. This we do, in order to allow overriding those styles easily externally,
                // without having to know these internal rules.
                ':where(:has(+ .MuiCardContent-root:not([aria-hidden="true"])))&': {
                    paddingBottom: `calc(${theme.spacing(4)} / 2)`,
                },
                // If this card content has a previous sibling, reduce padding-top
                // `:where` will reduce specifity to 0 and the `&` at the end will set it to the same level as
                // normal styles. This we do, in order to allow overriding those styles easily externally,
                // without having to know these internal rules.
                // These rules are split because it seems @emotion doesn't handle `:where()` correctly when there
                // is more than one selector in the rule.
                ':where(.MuiCardHeader-root:not([aria-hidden="true"]) + &)': {
                    paddingTop: `calc(${theme.spacing(4)} / 2)`,
                },
                ':where(.MuiCardContent-root:not([aria-hidden="true"]) + &)': {
                    paddingTop: `calc(${theme.spacing(4)} / 2)`,
                },
            }),
        },
    },
    MuiCardMedia: {
        styleOverrides: {
            root: {
                display: 'flex',
            },
        },
    },
    MuiCardActions: {
        styleOverrides: {
            root: ({ theme }) => ({
                borderTop: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                padding: theme.spacing(4),
            }),
        },
    },
    MuiDivider: {
        styleOverrides: {
            root: ({ ownerState: { thick }, theme }) => ({
                ...(thick && {
                    borderBottomWidth: theme.borders[2],
                }),
            }),
            fullWidth: ({ ownerState: { indent }, theme }) => ({
                ...(indent != null && {
                    marginLeft: (indent < 0 ? '-' : '') + theme.spacing(Math.abs(indent)),
                    marginRight: (indent < 0 ? '-' : '') + theme.spacing(Math.abs(indent)),
                }),
            }),
        },
    },
    MuiDrawer: {
        defaultProps: {
            slotProps: {
                paper: {
                    elevation: 16,
                },
            },
        },
    },
    MuiIconButton: {
        styleOverrides: {
            sizeSmall: {
                minWidth: `${BUTTON_HEIGHT_SMALL}px`,
                minHeight: `${BUTTON_HEIGHT_SMALL}px`,
            },
            sizeMedium: {
                minWidth: `${BUTTON_HEIGHT_MEDIUM}px`,
                minHeight: `${BUTTON_HEIGHT_MEDIUM}px`,
            },
            sizeLarge: {
                minWidth: `${BUTTON_HEIGHT_LARGE}px`,
                minHeight: `${BUTTON_HEIGHT_LARGE}px`,
            },
        },
    },
    MuiLink: {
        defaultProps: {
            color: 'secondary',
            component: LinkBehavior,
            underline: 'hover',
        },
    },
    MuiListItem: {
        defaultProps: {
            divider: true,
        },
        styleOverrides: {
            root: ({ ownerState, theme }) => ({
                ...(!ownerState.disableGutters &&
                    !ownerState.disablePadding &&
                    getListItemPadding(theme, ownerState.level)),
                ...(ownerState.active === false && {
                    color: theme.palette.primary['400'],
                }),
            }),
        },
    },
    MuiListItemButton: {
        defaultProps: {
            divider: true,
        },
        styleOverrides: {
            root: ({ ownerState, theme }) => ({
                ...(!ownerState.disableGutters &&
                    !ownerState.disablePadding &&
                    getListItemPadding(theme, ownerState.level)),
                ...(ownerState.active === false && {
                    color: theme.palette.primary['400'],
                }),
            }),
        },
    },
    MuiListItemText: {
        styleOverrides: {
            primary: {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            },
        },
    },
    MuiPagination: {
        styleOverrides: {
            root: {
                width: '100%',
            },
            ul: {
                justifyContent: 'center',
            },
        },
    },
    MuiTabs: {
        styleOverrides: {
            scroller: {
                height: '100%',
            },
            list: {
                height: '100%',
            },
        },
    },
    MuiTooltip: {
        defaultProps: {
            arrow: true,
            placement: 'top',
            // `as` swaps only the rendered component, keeping MUI's internal `TooltipPopper`
            // (and its per-placement arrow styles) in place, unlike a `slots.popper` override
            slotProps: { popper: { as: PopperComponent } },
        },
        styleOverrides: {
            arrow: ({ ownerState: { color }, theme }) => ({
                ...(color && {
                    color: alpha(theme.palette[color].main, TOOLTIP_ALPHA_VALUE),
                }),
            }),
            tooltip: ({ ownerState: { color }, theme }) => ({
                ...(color && {
                    color: theme.palette[color].contrastText,
                    backgroundColor: alpha(theme.palette[color].main, TOOLTIP_ALPHA_VALUE),
                }),
                ...theme.unstable_sx({ typography: 'medium' }),
            }),
        },
    },
    MuiTextField: {
        defaultProps: {
            size: 'small',
            fullWidth: true,
        },
    },
    // MUI X v9 renders date/time fields with `PickersTextField`, which does not inherit the
    // `MuiTextField` defaults above. Re-apply `size: 'small'` here so pickers keep the same
    // (smaller) height as the rest of the app's inputs.
    MuiPickersTextField: {
        defaultProps: {
            size: 'small',
        },
    },
    MuiSwitch: {
        defaultProps: {
            color: 'success',
            slotProps: {
                input: {
                    type: 'checkbox',
                },
            },
        },
    },
    MuiAutocomplete: {
        defaultProps: {
            fullWidth: true,
            size: 'small',
            slotProps: {
                popupIndicator: {
                    size: 'small',
                },
                clearIndicator: {
                    size: 'small',
                },
            },
        },
        styleOverrides: {
            tagSizeSmall: {
                height: 21,
            },
        },
    },
    MuiCheckbox: {
        defaultProps: {
            size: 'medium',
        },
    },
    MuiFormControl: {
        defaultProps: {
            fullWidth: true,
        },
    },
    MuiToggleButton: {
        styleOverrides: {
            root: ({ theme }) => ({
                '&.Mui-disabled': {
                    // This is the same as the default disabled color,
                    // but we need to set it again here because within MUI,
                    // the "selected" state is applied after the "disabled" state,
                    // and it looks not disabled anymore.
                    color: theme.palette.action.disabled,
                },
            }),
        },
    },
    MuiDialog: {
        styleOverrides: {
            paper: ({ theme }) => ({
                minWidth: theme.fixed.dialog.minWidth,
            }),
        },
    },
    MuiDialogTitle: {
        styleOverrides: {
            root: ({ theme }) => ({
                padding: theme.spacing(4),
            }),
        },
    },
    MuiDialogContent: {
        styleOverrides: {
            root: ({ theme }) => ({
                paddingTop: `${theme.spacing(2)}!important`,
            }),
        },
    },
    MuiDialogActions: {
        styleOverrides: {
            root: ({ theme }) => ({
                padding: `0 ${theme.spacing(4)} ${theme.spacing(4)}`,
            }),
        },
    },
};

export default components;
